import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, CheckCircle2, XCircle, Loader2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { scoreColor } from "@/components/ScoreGauge";

const GATE = 80;

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Enrollments() {
  const [sites, setSites] = useState(null);
  const navigate = useNavigate();
  const poll = useRef(null);

  const load = () => api.get("/sites").then((r) => setSites(r.data.filter((s) => s.source === "reachrep"))).catch(() => setSites([]));
  useEffect(() => { load(); poll.current = setInterval(load, 5000); return () => clearInterval(poll.current); }, []);

  const feed = (sites || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const gateOf = (s) => s.gate_threshold ?? GATE;
  const scored = feed.filter((s) => s.status === "ready");
  const passed = scored.filter((s) => (s.latest_score ?? s.gate_score ?? 0) >= gateOf(s)).length;

  return (
    <AppLayout>
      <div className="mb-6 flex items-center gap-2">
        <Zap className="h-6 w-6 text-emerald-500" />
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Enroll Timeline</h1>
          <p className="mt-1 text-sm text-muted-foreground">Live feed of ReachRep sites as they auto-enroll and clear the {GATE}+ visibility gate.</p>
        </div>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Live</span>
      </div>

      {sites && feed.length > 0 && (
        <div className="mb-6 grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-border bg-card p-4"><p className="font-mono text-2xl font-medium">{feed.length}</p><p className="text-xs text-muted-foreground">enrolled</p></div>
          <div className="rounded-lg border border-border bg-card p-4"><p className="font-mono text-2xl font-medium text-emerald-500">{passed}</p><p className="text-xs text-muted-foreground">passed gate</p></div>
          <div className="rounded-lg border border-border bg-card p-4"><p className="font-mono text-2xl font-medium text-amber-500">{scored.length - passed}</p><p className="text-xs text-muted-foreground">below gate</p></div>
        </div>
      )}

      {sites === null && <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>}

      {sites && feed.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-24 text-center" data-testid="enroll-empty">
          <Zap className="mb-4 h-10 w-10 text-muted-foreground" />
          <h3 className="font-display text-xl font-semibold">No enrollments yet</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">Sites promoted by the ReachRep pipeline will appear here in real time as they enroll.</p>
        </div>
      )}

      {sites && feed.length > 0 && (
        <div className="relative space-y-3 border-l border-border pl-6" data-testid="enroll-timeline">
          <AnimatePresence initial={false}>
            {feed.map((s) => {
              const score = s.latest_score ?? s.gate_score;
              const auditing = s.status === "auditing";
              const pass = !auditing && score != null && score >= gateOf(s);
              let host = s.url; try { host = new URL(s.url).hostname.replace("www.", ""); } catch {}
              return (
                <motion.div key={s.id} layout initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                  data-testid="enroll-row"
                  className="relative cursor-pointer rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/50"
                  onClick={() => navigate(`/site/${s.id}`)}>
                  <span className={`absolute -left-[31px] top-5 h-3 w-3 rounded-full ring-4 ring-background ${auditing ? "bg-primary" : pass ? "bg-emerald-500" : "bg-amber-500"}`} />
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-medium">{s.business_name || host}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{timeAgo(s.created_at)} · {s.platform || "analyzing…"}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {auditing ? (
                        <span className="flex items-center gap-1.5 text-sm text-primary"><Loader2 className="h-4 w-4 animate-spin" /> Auditing</span>
                      ) : (
                        <>
                          <span className="font-mono text-2xl font-medium" style={{ color: scoreColor(score) }}>{score ?? "—"}</span>
                          {pass ? (
                            <span className="flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400" data-testid="gate-pass"><CheckCircle2 className="h-3.5 w-3.5" /> Passed</span>
                          ) : (
                            <span className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="gate-fail"><XCircle className="h-3.5 w-3.5" /> Below gate</span>
                          )}
                        </>
                      )}
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </AppLayout>
  );
}
